import { MessageCircleQuestionIcon, PlusIcon, XIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasButton,
  CanvasInput,
  CanvasNumberInput,
  CanvasPercentageInput,
  CanvasSimpleSelect,
  CanvasSwitch,
  CanvasTextArea,
  PercentOperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  ComparisonOperator,
  comparisonOperators,
  Dimensions,
  metadataType,
  QuestionKind,
  questionKinds,
  TextQuestionProps,
  TextQuestionShape,
  textQuestionType,
} from "@/components/canvas-editor/shapes/shapes";
import { ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

/**
 * Questions per feed per hour, matching `DEFAULT_TEXT_QUESTIONS_PER_HOUR` in the
 * matcher's quota policy.
 *
 * Ten times the Natural Language node's budget, because the engine behind it costs
 * roughly three orders of magnitude less per question. Duplicated rather than fetched
 * for the same reason that one is: it is a sentence in a tooltip, not a reading. The
 * live number for a given feed is on the Rate limits page.
 */
const RATE_LIMIT_PER_HOUR = 20000;

const KIND_OPTIONS: { value: QuestionKind; label: string }[] = [
  { value: "noul", label: "Yes / no" },
  { value: "choice", label: "Pick one of my options" },
  { value: "score", label: "Rate on my scale" },
];

export function isTextQuestionShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is TextQuestionShape {
  return shape.type === textQuestionType;
}

export class TextQuestionTool extends ToolStateNode {
  static override id = textQuestionType;

  static override uiToolDef = (editor: Editor) => ({
    id: textQuestionType,
    icon: MessageCircleQuestionIcon,
    label: TextQuestionShapeUtil.docs.title,
    defaultColor: TextQuestionShapeUtil.docs.defaultColor,
    rateLimited: true,
    onSelect: () => editor.setCurrentTool(textQuestionType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={TextQuestion}
        util={TextQuestionShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: textQuestionType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class TextQuestionShapeUtil extends BindableUtil<TextQuestionShape> {
  static override migrations = createMigrations(textQuestionType);
  static override type = textQuestionType;

  static override docs = {
    defaultColor: "purple" as const,
    title: "Ask a Question",
    description: (
      <div className="flex flex-col gap-2">
        <div>
          Asks a question about each post and compares the answer against your
          threshold. Three kinds of question, and the kind decides what the
          number means.
        </div>
        <div>
          <span className="font-medium">Yes / no</span> gives the chance the
          answer is yes, 0&ndash;100%. This is the same shape as the Natural
          Language node, on a cheaper engine.
        </div>
        <div>
          <span className="font-medium">Pick one of my options</span> is the one
          nothing else here can do: you define the categories, and the node
          reads the chance the post belongs to the <em>one you choose</em> &mdash;
          not whichever one wins. So &ldquo;at least 70% likely to be about the
          NBA&rdquo; still matches a post the model thinks is more likely about
          college basketball.
        </div>
        <div>
          <span className="font-medium">Rate on my scale</span> takes ordered
          levels and answers with a position on them. The threshold is a{" "}
          <span className="font-medium">level number, not a percentage</span>:
          with three levels you compare against 0, 1 or 2, and the answer can
          land between them. Do not read a level as a measurement &mdash; the
          model is good at ordering, not at arithmetic.
        </div>
        <div>
          <span className="font-medium">Rate limited.</span> Every feed gets{" "}
          <span className="font-medium">
            {RATE_LIMIT_PER_HOUR.toLocaleString()} questions an hour
          </span>
          , and you are charged only for posts this node actually judges &mdash;
          so putting cheaper conditions above it is what keeps a feed inside the
          budget. Repeats of a post already judged are free. When the budget runs
          out, &ldquo;Admit posts when over budget&rdquo; decides what the node
          answers until the hour turns over.
        </div>
        <div>
          <span className="font-medium">Write it literally.</span> The model
          answers the question you wrote, not the one you meant. If you look at
          a wrong answer and find yourself explaining what you really meant,
          that explanation belongs in the question. Keep numbers, dates and
          counting out of it &mdash; use a Compare node for those.
        </div>
      </div>
    ),
  };

  override getType() {
    return textQuestionType;
  }

  static override props: RecordProps<TextQuestionShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    kind: T.literalEnum(...questionKinds),
    instructions: T.string,
    options: T.arrayOf(
      T.object({ name: T.string, description: T.string }),
    ),
    select: T.string,
    levels: T.arrayOf(T.string),
    operator: T.literalEnum(...comparisonOperators),
    threshold: T.number,
    overQuota: T.boolean.optional(),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 340,
      minW: 400,
      h: 235,
      kind: "noul" as const,
      instructions: "",
      // Two of each, because one option is not a choice and one level is not a scale —
      // both engines refuse such a question at parse time, so starting with one row
      // would hand the author a node that cannot save.
      options: [
        { name: "", description: "" },
        { name: "", description: "" },
      ],
      select: "",
      levels: ["", ""],
      threshold: 0.75,
      operator: ">=" as const,
      overQuota: false,
      metadata: undefined,
    };
  }

  override component(shape: TextQuestionShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <TextQuestion
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function TextQuestion({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: TextQuestionShape;
  className?: string;
  updateShape: (
    shape: TextQuestionShape,
    props: Partial<TextQuestionProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  const { kind, options, levels, select } = shape.props;

  const namedOptions = options.filter((option) => option.name.trim() !== "");
  // A `select` naming an option that no longer exists can never match, and both engines
  // refuse it at parse time. Surfacing it on the node is cheaper than a failed save.
  const selectIsDangling =
    kind === "choice" &&
    select.trim() !== "" &&
    !namedOptions.some((option) => option.name.trim() === select);

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color || TextQuestionShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={TextQuestionShapeUtil.docs}
        />

        <BlockBody>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <CanvasSimpleSelect
              aria-label="Question kind"
              value={kind}
              options={KIND_OPTIONS}
              onValueChange={(value) =>
                updateShape(shape, { kind: value as QuestionKind })
              }
            />

            <CanvasTextArea
              value={shape.props.instructions}
              placeholder={
                kind === "noul"
                  ? "e.g. is this a job posting for a permanent role"
                  : kind === "choice"
                    ? "e.g. which sport is this post about"
                    : "e.g. how technical is this post"
              }
              maxLength={1000}
              onValueChange={(instructions) =>
                updateShape(shape, { instructions })
              }
            />

            {kind === "choice" && (
              <div className="flex flex-col gap-1">
                {options.map((option, index) => (
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  <div key={index} className="flex gap-1 items-center">
                    <CanvasInput
                      aria-label={`Option ${index + 1} name`}
                      value={option.name}
                      placeholder="option"
                      className="w-28 shrink-0"
                      onValueChange={(name) => {
                        const next = options.map((o, i) =>
                          i === index ? { ...o, name } : o,
                        );
                        // Keep `select` pointing at the row the author renamed, rather
                        // than silently detaching it.
                        const patch: Partial<TextQuestionProps> = {
                          options: next,
                        };
                        if (select === option.name) patch.select = name;
                        updateShape(shape, patch);
                      }}
                    />
                    <CanvasInput
                      aria-label={`Option ${index + 1} description`}
                      value={option.description}
                      placeholder="what it means (optional)"
                      className="flex-1"
                      onValueChange={(description) =>
                        updateShape(shape, {
                          options: options.map((o, i) =>
                            i === index ? { ...o, description } : o,
                          ),
                        })
                      }
                    />
                    <CanvasButton
                      aria-label={`Remove option ${index + 1}`}
                      // Two is the floor the engines enforce; below it the question
                      // cannot be answered at all.
                      disabled={options.length <= 2}
                      className="px-1"
                      onClick={() =>
                        updateShape(shape, {
                          options: options.filter((_, i) => i !== index),
                        })
                      }
                    >
                      <XIcon />
                    </CanvasButton>
                  </div>
                ))}
                <CanvasButton
                  className="self-start px-2 text-xs"
                  onClick={() =>
                    updateShape(shape, {
                      options: [...options, { name: "", description: "" }],
                    })
                  }
                >
                  <PlusIcon /> Add option
                </CanvasButton>

                <div className="flex gap-2 items-center">
                  <SimpleTooltip message="Which option's likelihood this condition reads. Not necessarily the one the model thinks is most likely — that is what lets you ask 'at least 70% about the NBA' on a post that scores higher for something else.">
                    <span className="text-xs text-muted-foreground shrink-0">
                      Chance it is
                    </span>
                  </SimpleTooltip>
                  <CanvasSimpleSelect
                    aria-label="Selected option"
                    value={select}
                    options={namedOptions.map((option) => ({
                      value: option.name.trim(),
                      label: option.name.trim(),
                    }))}
                    onValueChange={(value) =>
                      updateShape(shape, { select: value })
                    }
                  />
                </div>
                {selectIsDangling && (
                  <div className="text-xs text-destructive">
                    &ldquo;{select}&rdquo; is not one of the options, so this
                    condition can never match.
                  </div>
                )}
              </div>
            )}

            {kind === "score" && (
              <div className="flex flex-col gap-1">
                {levels.map((level, index) => (
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  <div key={index} className="flex gap-1 items-center">
                    <span className="text-xs text-muted-foreground w-4 shrink-0 text-right">
                      {index}
                    </span>
                    <CanvasInput
                      aria-label={`Level ${index}`}
                      value={level}
                      placeholder={
                        index === 0 ? "lowest level" : "next level up"
                      }
                      className="flex-1"
                      onValueChange={(value) =>
                        updateShape(shape, {
                          levels: levels.map((l, i) =>
                            i === index ? value : l,
                          ),
                        })
                      }
                    />
                    <CanvasButton
                      aria-label={`Remove level ${index}`}
                      disabled={levels.length <= 2}
                      className="px-1"
                      onClick={() =>
                        updateShape(shape, {
                          levels: levels.filter((_, i) => i !== index),
                        })
                      }
                    >
                      <XIcon />
                    </CanvasButton>
                  </div>
                ))}
                <CanvasButton
                  className="self-start px-2 text-xs"
                  onClick={() =>
                    updateShape(shape, { levels: [...levels, ""] })
                  }
                >
                  <PlusIcon /> Add level
                </CanvasButton>
              </div>
            )}

            <div className="flex gap-2">
              <PercentOperatorSelect
                value={shape.props.operator}
                onValueChange={(v) =>
                  updateShape(shape, { operator: v as ComparisonOperator })
                }
              />
              {kind === "score" ? (
                <SimpleTooltip
                  variant="markdown"
                  message="A **level number**, not a percentage. With three levels, compare against 0, 1 or 2. The answer can land between levels — a 1.6 means the post sits between your second and third level, nearer the third."
                >
                  <CanvasNumberInput
                    aria-label="Level"
                    value={shape.props.threshold}
                    placeholder="Level"
                    className="flex-1"
                    onValueChange={(value) =>
                      updateShape(shape, { threshold: value ?? 0 })
                    }
                  />
                </SimpleTooltip>
              ) : (
                <SimpleTooltip
                  variant="markdown"
                  message="The likelihood threshold. If moving it has no effect, move it a long way rather than a little, and check the wording of the question above."
                >
                  <CanvasPercentageInput
                    aria-label="Threshold"
                    value={shape.props.threshold}
                    placeholder="Threshold"
                    className="flex-1"
                    onValueChange={(value) =>
                      updateShape(shape, { threshold: value })
                    }
                  />
                </SimpleTooltip>
              )}
            </div>

            <div className="flex items-center justify-between gap-2">
              <SimpleTooltip
                message={`Each feed can ask ${RATE_LIMIT_PER_HOUR.toLocaleString()} questions an hour. Off: once the budget is gone this condition matches nothing and the feed goes quiet. On: it stops filtering and lets everything through.`}
              >
                <span className="text-xs text-muted-foreground">
                  Admit posts when over budget
                </span>
              </SimpleTooltip>
              <CanvasSwitch
                checked={shape.props.overQuota ?? false}
                onCheckedChange={(overQuota) =>
                  updateShape(shape, { overQuota })
                }
              />
            </div>
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
